import {BrowserRouter as Router, Route, Routes, useLocation} from "react-router-dom";
import ConsentPage from "./components/auth/signup/ConsentPage";
import PersonalInfoPage from "./components/auth/signup/PersonalInfoPage";
import AccountInfoPage from "./components/auth/signup/AccountInfoPage";
import SignInPage from "./components/auth/signIn/SignInPage";
import PageLink from "./components/home/PageLink";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import FindIdPage from "./components/auth/findIDorPW/FindIdPage";
import EmailForm from "./components/auth/findIDorPW/EmailForm";
import PhoneForm from "./components/auth/findIDorPW/PhoneForm";
import FindIdResultPage from "./components/auth/findIDorPW/FindIdResultPage";
import FindPwPage from "./components/auth/findIDorPW/FindPwPage";
import ResetPasswordPage from "./components/auth/findIDorPW/ResetPasswordPage";
import { AuthProvider } from "./components/auth/AuthContext";
import { useEffect } from "react";
import useSignInStore from "./store/SignInStore";
import MainPage from "./pages/MainPage";
import Footer from "./components/rootlayout/Footer";
import Header from "./components/rootlayout/Header";
import ProductPage from "./pages/ProductPage";
import "./components/styles/GlobalMobileStyles.css";
import AdminPage from "./pages/AdminPage";
import ProductDetail from "./pages/ProductDetail";
import CompanyIntroPage from "./pages/CompanyIntroPage";
import CustomProductPage1 from "./pages/CustomProductPage";
import CustomProductSecondPage from "./pages/CustomProductSecondPage";
import ManualPage from "./pages/ManualPage";
import CustomerCenterPage from "./pages/CustomerCenterPage";
import FAQPage from "./pages/FQAPage";
import SearchPage from "./pages/SearchPage";
import CartPage from "./pages/CartPage";
import MyPage from "./pages/MyPage";
import MyInfoPage from "./components/mypage/MyInfoPage";
import NotificationSettingsPage from "./components/mypage/NotificationSettingsPage";
import PointManagementPage from "./components/mypage/PointManagementPage";
import CouponManagementPage from "./components/mypage/CouponManagementPage";
import MembershipWithdrawal from "./components/mypage/MembershipWithdrawal";
import PurchaseHistory from "./components/mypage/PurchaseHistory";
import ReviewManagementPage from "./components/mypage/ReviewManagementPage";
import ReviewAvailablePage from "./components/mypage/ReviewAvailablePage";
import MyReviewsPage from "./components/mypage/MyReviewsPage";
import QnaManagementPage from "./components/mypage/QnaManagementPage";
import SubscriptionManagementPage from "./components/mypage/SubscriptionManagementPage";
import CustomOrderHistory from "./components/mypage/CustomOrderHistory";
import SubscriptionDetail from "./components/mypage/SubscriptionDetail";
import OrderDetail from "./components/mypage/OrderDetail";
import OrderPayment from "./pages/OrderPayment";
import OrderSuccess from "./components/payment/OrderSuccess";
import OrderFailed from "./components/payment/OrderFailed";
import CustomProductDetail from "./pages/CustomProductDetail";
import PrivacyPolicy from "./components/raw/PrivacyPolicyPage";
import TermOfUse from "./components/raw/TermOfUsePage";
import AdminProductManager from "./components/admin/AdminProductManager";
import AdminUserManager from "./components/admin/AdminUserManager";
import AdminSiteSettings from "./components/admin/AdminSiteSetting";
import AdminCouponCreate from "./components/admin/AdminCouponCreate";
import AdminOrderManager from "./components/admin/adminOrderManager";
import OAuthSuccessPage from "./components/auth/signIn/OAuthSuccessPage";
import { ReviewProvider } from './context/ReviewContext';
import KmongAutoReply from "./components/kmong/KmongAutoReply";
import ProjectManager from "./pages/ProjectManager";

const queryClient = new QueryClient();

export const API_BASE_URL = process.env.REACT_APP_API_ROOT;
export const HOME_URL = process.env.REACT_APP_HOME_URL;

const AppContent = () => {
    const location = useLocation();
    const isLoginPage = location.pathname === "/login";
    const isKmongPage = location.pathname === "/kmong-management";
    const isProjectPage = location.pathname === "/project-manager";

    return (
        <div id="App" className="flex flex-col min-h-screen">
            {!isLoginPage && !isKmongPage && !isProjectPage && <Header />}
            <div className="flex-1">
                <Routes>
                    {/* 크몽 자동 답변 시스템 */}
                    <Route path="/kmong-management" element={<KmongAutoReply />} />
                    
                    {/* 프로젝트 관리 시스템 */}
                    <Route path="/project-manager" element={<ProjectManager />} />

                    {/* 관리자 페이지 */}
                    <Route path="/admin" element={<AdminPage />} />
                    <Route path="/admin/orders" element={<AdminOrderManager />} />
                    <Route path="/admin/products" element={<AdminProductManager />} />
                    <Route path="/admin/users" element={<AdminUserManager />} />
                    <Route path="/admin/settings" element={<AdminSiteSettings />} />
                    <Route path="/admin/coupons" element={<AdminCouponCreate />} />

                    <Route path="/" element={<MainPage />} />
                    <Route path="/signup" element={<ConsentPage />} />
                    <Route path="/signup/account-info" element={<AccountInfoPage />} />
                    <Route path="/signup/personal-info" element={<PersonalInfoPage />} />
                    <Route path="/login" element={<SignInPage />} />
                    <Route path="/oauth2/success" element={<OAuthSuccessPage />} />
                    <Route path="/find-id" element={<FindIdPage />} />
                    <Route path="/find-id/email" element={<EmailForm />} />
                    <Route path="/find-id/phone" element={<PhoneForm />} />
                    <Route path="/find-id/result" element={<FindIdResultPage />} />
                    <Route path="/find-pw" element={<FindPwPage />} />
                    <Route path="/find-pw/reset-password" element={<ResetPasswordPage />} />
                    <Route path="/products" element={<ProductPage />} />
                    <Route path="/products/:id" element={<ProductDetail />} />
                    <Route path="/about" element={<CompanyIntroPage />}></Route>
                    <Route path="/custom-product-first" element={<CustomProductPage1 />} />
                    <Route path="/custom-product-second" element={<CustomProductSecondPage />} />
                    <Route path="/manual" element={<ManualPage />} />
                    <Route path="/service" element={<CustomerCenterPage />} />
                    <Route path="/FAQ" element={<FAQPage />} />
                    <Route path="/search" element={<SearchPage />} />
                    <Route path="/cart" element={<CartPage />} />
                    <Route path="/mypage" element={<MyPage />} />
                    <Route path="/mypage/my-info" element={<MyInfoPage />} />
                    <Route path="/mypage/notification" element={<NotificationSettingsPage />} />
                    <Route path="/mypage/check-point" element={<PointManagementPage />} />
                    <Route path="/mypage/check-coupon" element={<CouponManagementPage />} />
                    <Route path="/mypage/withdrawal" element={<MembershipWithdrawal />} />
                    {/* 구매 내역 */}
                    <Route path="/mypage/purchase-list" element={<PurchaseHistory />} />
                    <Route path={"/mypage/purchase/detail/:id"} element={<OrderDetail />} />
                    <Route path="/mypage/review-list" element={<ReviewManagementPage />} />
                    {/* 리뷰 가능한 상품*/}
                    <Route path="/mypage/review-available-list" element={<ReviewAvailablePage />} />
                    {/*    내가 작성한 리뷰 */}
                    <Route path="/mypage/review-written-list" element={<MyReviewsPage />} />
                    {/*    Q&A 관리 */}
                    <Route path={"/mypage/qna"} element={<QnaManagementPage />} />
                    {/*    구독 관리 */}
                    <Route path={"/mypage/subscription"} element={<SubscriptionManagementPage />} />
                    <Route path={"/mypage/subscription/detail/:subscriptionId"} element={<SubscriptionDetail />} />
                    {/*    맞춤상품 신청내역 */}
                    <Route path={"/mypage/custom-order"} element={<CustomOrderHistory />} />
                    <Route path={"/mypage/custom-order/:applicationArticleId"} element={<CustomProductDetail />} />
                    {/* 결제 페이지 */}
                    <Route path="/order/checkout" element={<OrderPayment />} />
                    <Route path="/order/success" element={<OrderSuccess />} />
                    <Route path="/order/failed" element={<OrderFailed />} />
                    {/* 개인정보 처리방침 */}
                    <Route path="/privacy-policy" element={<PrivacyPolicy title="개인정보 처리방침" />} />
                    {/* 이용약관 */}
                    <Route path="/term-of-use" element={<TermOfUse title="이용약관" />} />
                    {/* 쿠키 정책 */}

                </Routes>
            </div>
            {!isKmongPage && !isProjectPage && <Footer />}
        </div>
    );
};

function App() {
    return (
        <QueryClientProvider client={queryClient}>
            <AuthProvider>
                <ReviewProvider>
                    <Router>
                        <AppContent />
                    </Router>
                </ReviewProvider>
            </AuthProvider>
        </QueryClientProvider>
    );
}

export default App;